import type { CompensationDeduction, CompensationState } from './types';

export const LO_COMP_REDUCTION_TYPE = 'lo_comp_reduction' as const;
export const LO_COMP_REDUCTION_LABEL = 'LO Comp Reduction';
const COMP_PERCENT_TOLERANCE = 0.001;

export type LoCompReductionNormalizationStatus = 'applied' | 'skipped' | 'blocked';
export type LoCompReductionImportSource = 'encompass_current' | 'delivery_lock';

export interface LoCompReductionImportMetadata {
  baseLoCompBps: number;
  rawReductionBps: number;
  appliedReductionBps: number;
  reductionAmount: number;
  effectiveCompPercent: number;
  source: LoCompReductionImportSource;
}

export interface NormalizeLoCompReductionForReconInput {
  compensation: CompensationState;
  metadata: LoCompReductionImportMetadata | null;
  reconStatus: 'draft' | 'finalized' | 'pushed';
  isFreshNoComp: boolean;
}

export interface NormalizeLoCompReductionForReconResult {
  compensation: CompensationState;
  status: LoCompReductionNormalizationStatus;
  reason?: string;
}

export function roundToCents(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function buildLoCompReductionImportMetadata(params: {
  loanAmount: number;
  baseLoCompBps: number;
  rawReductionBps: number;
  source: LoCompReductionImportSource;
}): LoCompReductionImportMetadata | null {
  const loanAmount = Number(params.loanAmount) || 0;
  const baseLoCompBps = Number(params.baseLoCompBps) || 0;
  const rawReductionBps = Number(params.rawReductionBps) || 0;

  if (loanAmount <= 0 || baseLoCompBps <= 0) {
    return null;
  }

  const appliedReductionBps = Math.min(Math.max(rawReductionBps, 0), baseLoCompBps);
  const effectiveLoCompBps = baseLoCompBps - appliedReductionBps;

  return {
    baseLoCompBps,
    rawReductionBps,
    appliedReductionBps,
    reductionAmount: roundToCents((loanAmount * appliedReductionBps) / 10000),
    effectiveCompPercent: effectiveLoCompBps / 100,
    source: params.source
  };
}

export function normalizeLoCompReductionForRecon(
  input: NormalizeLoCompReductionForReconInput
): NormalizeLoCompReductionForReconResult {
  const { compensation, metadata, reconStatus, isFreshNoComp } = input;

  if (reconStatus === 'finalized' || reconStatus === 'pushed') {
    return { compensation, status: 'skipped', reason: 'terminal_recon' };
  }

  const { compensation: dedupedCompensation, reduction } = dedupeReduction(compensation);

  if (!metadata) {
    return { compensation: dedupedCompensation, status: 'skipped', reason: 'missing_metadata' };
  }

  const appliedCompensation = applyLoCompReductionToCompensation(dedupedCompensation, metadata, 'product_pricing');
  const baseCompPercent = metadata.baseLoCompBps / 100;

  if (!appliedCompensation) {
    return { compensation: dedupedCompensation, status: 'skipped', reason: 'no_positive_reduction' };
  }

  if (reduction) {
    if (
      dedupedCompensation.type === 'percentage' &&
      approximatelyEqual(dedupedCompensation.value, metadata.effectiveCompPercent)
    ) {
      return {
        compensation: dedupedCompensation,
        status: 'blocked',
        reason: 'saved_reduction_with_effective_comp'
      };
    }

    return { compensation: dedupedCompensation, status: 'skipped', reason: 'saved_reduction_preserved' };
  }

  if (isFreshNoComp) {
    return {
      compensation: appliedCompensation,
      status: 'applied',
      reason: 'fresh_import'
    };
  }

  if (
    dedupedCompensation.type === 'percentage' &&
    approximatelyEqual(dedupedCompensation.value, metadata.effectiveCompPercent)
  ) {
    return {
      compensation: appliedCompensation,
      status: 'applied',
      reason: 'converted_effective_comp'
    };
  }

  if (
    dedupedCompensation.type === 'percentage' &&
    approximatelyEqual(dedupedCompensation.value, baseCompPercent)
  ) {
    return {
      compensation: appliedCompensation,
      status: 'applied',
      reason: 'converted_base_comp'
    };
  }

  return { compensation: dedupedCompensation, status: 'skipped', reason: 'manual_or_mismatched_comp' };
}

export function applyLoCompReductionToCompensation(
  compensation: CompensationState,
  metadata: LoCompReductionImportMetadata | null,
  source: NonNullable<CompensationDeduction['source']>
): CompensationState | null {
  if (!metadata) {
    return null;
  }

  const baseLoCompBps = Number(metadata.baseLoCompBps) || 0;
  const appliedReductionBps = Number(metadata.appliedReductionBps) || 0;
  const reductionAmount = roundToCents(Number(metadata.reductionAmount) || 0);

  if (baseLoCompBps <= 0 || appliedReductionBps <= 0 || reductionAmount <= 0) {
    return null;
  }

  const otherDeductions = (compensation.deductions ?? [])
    .filter(d => d.type !== LO_COMP_REDUCTION_TYPE);

  return {
    ...compensation,
    type: 'percentage',
    value: baseLoCompBps / 100,
    deductions: [
      makeReductionDeduction(reductionAmount, source),
      ...otherDeductions
    ]
  };
}

function dedupeReduction(compensation: CompensationState): {
  compensation: CompensationState;
  reduction: CompensationDeduction | null;
} {
  const deductions = compensation.deductions ?? [];
  const reductions = deductions.filter(d => d.type === LO_COMP_REDUCTION_TYPE);
  if (reductions.length === 0) {
    return { compensation, reduction: null };
  }

  const preferred = reductions.find(d => Number(d.deductionAmount) > 0) ?? reductions[0];
  const amount = roundToCents(Number(preferred.deductionAmount) || 0);
  const reduction = makeReductionDeduction(amount, preferred.source ?? 'saved');
  const otherDeductions = deductions.filter(d => d.type !== LO_COMP_REDUCTION_TYPE);

  return {
    compensation: {
      ...compensation,
      deductions: [reduction, ...otherDeductions]
    },
    reduction
  };
}

function makeReductionDeduction(
  amount: number,
  source: NonNullable<CompensationDeduction['source']>
): CompensationDeduction {
  return {
    type: LO_COMP_REDUCTION_TYPE,
    label: LO_COMP_REDUCTION_LABEL,
    sourceAmount: amount,
    deductionAmount: amount,
    readOnly: true,
    source
  };
}

function approximatelyEqual(a: number, b: number): boolean {
  return Math.abs((Number(a) || 0) - (Number(b) || 0)) <= COMP_PERCENT_TOLERANCE;
}
